import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from '../components/Loader';
import Logo from '../images/school_logo.png';
import '../styles/ClassDetailsPage.css';
import ClassInfo from '../components/ClassInfo';
import AddStudentsToClassModal from '../components/AddStudentsToClassModal';
import AddSubjectsToClassModal from '../components/AddSubjectsToClassModal';
import AddTeacherToSubjectModal from '../components/AddTeachersToSubjectModal';
import AssignTeachersToSubjectsModal from '../components/AssignTeachersToSubjectsModal';
import AddQuestionsToSubjectModal from '../components/AddQuestionsToSubjectModal';
import AddPDFToSubjectModal from '../components/AddPDFToSubjectModal';
import PromoteClassModal from '../components/PromoteClassModal';
import Modal from 'react-modal';
import { useUserAuth } from "../context/UserAuthContext";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import SuccessModal from './SuccessModal';
import { SCHOOL_ACRONYM, SCHOOL_NAME } from '../Variables.js';
import generateReceiptPDF from './PaymentReceiptGenerator';

Modal.setAppElement('#root');

const ClassDetailsPage = () => {
  const { id } = useParams();
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showBulkTeacherModal, setShowBulkTeacherModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [feesAmount, setFeesAmount] = useState(0);
  const [isEditStudentFeesModalOpen, setIsEditStudentFeesModalOpen] = useState(false);
  const [isPayStudentFeesModalOpen, setIsPayStudentFeesModalOpen] = useState(false);
  const [currentStudentFees, setCurrentStudentFees] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [payerName, setPayerName] = useState('');
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);  
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [paymentFor, setPaymentFor] = useState(['School fees']);
  const [otherPaymentFor, setOtherPaymentFor] = useState('');
  const [currentTerm, setCurrentTerm] = useState(null);
  const paymentForOptions = [
    'School fees',
    'Outstanding balance',
    'Stationery',
    'Uniform',
    'Sportswear',
    'Cardigan',
    'Charges',
    'Others'
  ];

  const navigate = useNavigate();

  const fetchData = async () => {
    setError("");
    setSuccessMessage("");
    setShowModal(false);
    setLoading(true);
    try {
      const classRef = doc(db, "classes", id);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) {
        setError("Class not found");
        setShowModal(true);
        setLoading(false);
        return;
      }
  
      const classData = classSnap.data();

      const currentTermRef = doc(db, 'current-term', 'current');
      const currentTermSnap = await getDoc(currentTermRef);

      if (currentTermSnap.exists()) {
        setCurrentTerm(currentTermSnap.data());
      } else {
        console.warn("No current term set");
      }
  
      const [sectionDoc, sessionDoc, termDoc] = await Promise.all([
        classData.section ? getDoc(doc(db, "sections", classData.section)) : null,
        classData.session ? getDoc(doc(db, "sessions", classData.session)) : null,
        classData.term ? getDoc(doc(db, "terms", classData.term)) : null,
      ]);
  
      const sectionName = sectionDoc?.exists() ? sectionDoc.data().name : "Unknown section";
      const sessionName = sessionDoc?.exists() ? sessionDoc.data().name : "Unknown session";
      const termName = termDoc?.exists() ? termDoc.data().name : "Unknown term";
  
      let formMasterData = null;
    
      if (classData.formMaster && typeof classData.formMaster === 'string' && classData.formMaster.trim() !== '') {
        try {
          const formMasterDoc = await getDoc(doc(db, "users", classData.formMaster));
          if (formMasterDoc.exists() && ['Teacher', 'Cashier', 'Admin'].includes(formMasterDoc.data().status)) {
            formMasterData = { id: formMasterDoc.id, ...formMasterDoc.data() };
          }
        } catch (error) {
          setError("Error fetching class teacher");
          setShowModal(true);
        }
      }
  
      const studentsRef = collection(db, "students");
      const allStudentsSnap = await getDocs(studentsRef);
      const allStudents = allStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filteredStudents = allStudents.filter(student => classData.students?.includes(student.id));
      setStudents(filteredStudents);
  
      const subjectsRef = collection(db, "subjects");
      const allSubjectsSnap = await getDocs(subjectsRef);
      const allSubjects = allSubjectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Create a map of subject IDs to their questionSetId from class data
      const classSubjectMap = {};
      classData.subjects?.forEach(subject => {
        classSubjectMap[subject.id] = {
          questionSetId: subject.questionSetId || null,
          pdfLink: subject.pdfLink || null
        };
      });
  
      // Merge the subject data with the questionSetId from class data
      const filteredSubjects = allSubjects
        .filter(subject => classData.subjects?.some(s => s.id === subject.id))
        .map(subject => ({
          ...subject,
          questionSetId: classSubjectMap[subject.id]?.questionSetId || null,
          pdfLink: classSubjectMap[subject.id]?.pdfLink || null
        }));
  
      setSubjects(filteredSubjects);
  
      const teachersRef = collection(db, "users");
      const teachersSnap = await getDocs(teachersRef);
      const teachers = teachersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
      setTeachers(teachers);
  
      setClassInfo({
        ...classData,
        section: sectionName,
        sectionId: classData.section || '',
        session: sessionName,
        term: termName,
        formMaster: formMasterData,
      });
    } catch (error) {
      setError("Class not found");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [id]);
  
  const financialTotals = useMemo(() => {
    let expectedIncome = 0;
    let actualIncome = 0;
    let outstandingBalance = 0;

    students.forEach(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      const balance = fees - paid;

      expectedIncome += fees;
      actualIncome += paid;
      outstandingBalance += balance;
    });

    return {
      expectedIncome,
      actualIncome,
      outstandingBalance
    };
  }, [students, id]);

  const generateFeesReportPDF = () => {
    // Separate students into those with outstanding balances and those fully paid
    const studentsWithBalance = students.filter(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      return (fees - paid) > 0;
    });

    const studentsFullyPaid = students.filter(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      return fees > 0 && (fees - paid) <= 0;
    });

    if (students.length === 0) {
      setError("No students enrolled in this class");
      setShowModal(true);
      return;
    }

    // Calculate totals for outstanding balances
    let totalFeesOutstanding = 0;
    let totalPaidOutstanding = 0;
    let totalBalanceOutstanding = 0;

    const outstandingTableData = studentsWithBalance.map((student, index) => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      const balance = fees - paid;

      totalFeesOutstanding += fees;
      totalPaidOutstanding += paid;
      totalBalanceOutstanding += balance;

      return [
        index + 1,
        `${student.firstName} ${student.lastName}`,
        student.fathersName || 'N/A',
        student.fathersPhone1 || 'N/A',
        fees.toLocaleString(),
        paid.toLocaleString(),
        balance.toLocaleString()
      ];
    });

    const outstandingTotalsRow = [
      '',
      'TOTAL',
      '',
      '',
      totalFeesOutstanding.toLocaleString(),
      totalPaidOutstanding.toLocaleString(),
      totalBalanceOutstanding.toLocaleString()
    ];

    // Calculate totals for fully paid students
    let totalFeesPaid = 0;
    let totalPaidFully = 0;

    const fullyPaidTableData = studentsFullyPaid.map((student, index) => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;

      totalFeesPaid += fees;
      totalPaidFully += paid;

      return [
        index + 1,
        `${student.firstName} ${student.lastName}`,
        student.fathersName || 'N/A',
        student.fathersPhone1 || 'N/A',
        fees.toLocaleString(),
        paid.toLocaleString()
      ];
    });

    const fullyPaidTotalsRow = [
      '',
      'TOTAL',
      '',
      '',
      totalFeesPaid.toLocaleString(),
      totalPaidFully.toLocaleString()
    ];

    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString();

    doc.setFontSize(18);
    doc.setFont('helvetica', 'normal');
    doc.text(`${SCHOOL_ACRONYM} Fees Report`, 105, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text(`Class: ${classInfo.name} (${classInfo.session}-${classInfo.term} Term)`, 105, 25, { align: 'center' });
    doc.text(`Generated on: ${currentDate}`, 105, 32, { align: 'center' });
    doc.text(`Cashier: ${userData.firstName} ${userData.lastName}`, 105, 39, { align: 'center' });

    let startY = 45;

    // Outstanding Fees Table
    if (studentsWithBalance.length > 0) {
      doc.setFontSize(14);
      doc.text('Students with Outstanding Fees', 14, startY);
      startY += 7;

      autoTable(doc, {
        head: [['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees', 'Paid', 'Balance']],
        body: [...outstandingTableData, outstandingTotalsRow],
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 10,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 10, minCellHeight: 10, textColor: [0, 0, 0] },
          1: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
          2: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
          3: { cellWidth: 30, minCellHeight: 10, textColor: [0, 0, 0] },
          4: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] },
          5: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] },
          6: { cellWidth: 20, minCellHeight: 10, textColor: [255, 0, 0] }
        },
        didDrawPage: function (data) {
          startY = data.cursor.y + 10;
        }
      });
    } else {
      doc.setFontSize(14);
      doc.text('Students with Outstanding Fees', 14, startY);
      doc.setFontSize(12);
      doc.text('No students with outstanding balances', 14, startY + 7);
      startY += 14;
    }

    // Fully Paid Fees Table
    if (studentsFullyPaid.length > 0) {
      doc.setFontSize(14);
      doc.text('Students with Fully Paid Fees', 14, startY);
      startY += 7;

      autoTable(doc, {
        head: [['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees', 'Paid']],
        body: [...fullyPaidTableData, fullyPaidTotalsRow],
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 10,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 10, minCellHeight: 10, textColor: [0, 0, 0] },
          1: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
          2: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
          3: { cellWidth: 30, minCellHeight: 10, textColor: [0, 0, 0] },
          4: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] },
          5: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] }
        },
        didDrawPage: function (data) {
          startY = data.cursor.y + 10;
        }
      });
    } else {
      doc.setFontSize(14);
      doc.text('Students with Fully Paid Fees', 14, startY);
      doc.setFontSize(12);
      doc.text('No students with fully paid fees', 14, startY + 7);
      startY += 14;
    }

    // Add summary
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Expected Income: N ${financialTotals.expectedIncome.toLocaleString()}`, 14, startY);
    doc.text(`Total Actual Income: N ${financialTotals.actualIncome.toLocaleString()}`, 14, startY + 6);
    doc.text(`Total Outstanding: N ${financialTotals.outstandingBalance.toLocaleString()}`, 14, startY + 12);

    doc.save(`Fees_Report_${classInfo.name}_${currentDate.replace(/[/:, ]/g, '_')}.pdf`);
  };

  const generateStudentPassesPDF = () => {
    const studentsWithPass = students.filter(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      return fees > 0 && (fees - paid) <= 0;
    });
  
    if (studentsWithPass.length === 0) {
      setError("No students with complete payments");
      setShowModal(true);
      return;
    }
  
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Set initial position
    let yPos = 20;
    let xPos = 15;
    let passesInRow = 0;    
  
    // Configure text wrapping
    const maxSchoolNameWidth = 60; // Maximum width for school name
    const lineHeight = 5; // Height between lines
  
    studentsWithPass.forEach((student, index) => {
      // Add new page if we're at the bottom
      if (yPos > doc.internal.pageSize.getHeight() - 50) {
        doc.addPage();
        yPos = 20;
        xPos = 15;
        passesInRow = 0;
      }
      
      // Move to next row if current row is full
      if (passesInRow >= 2) {
        yPos += 50;
        xPos = 15;
        passesInRow = 0;
      }
      
      // Draw pass border
      doc.rect(xPos, yPos, 85, 45);

      // Add faded logo background (watermark)
      doc.setGState(new doc.GState({opacity: 0.2})); // Set transparency
      doc.addImage(Logo, 'PNG', xPos + 50, yPos + 10, 30, 30);
      doc.setGState(new doc.GState({opacity: 1})); // Reset transparency
      
      // School logo and name
      doc.addImage(Logo, 'PNG', xPos + 5, yPos + 5, 15, 15);
      doc.setFontSize(10);
      
      // Split school name into multiple lines if needed
      const splitSchoolName = doc.splitTextToSize(SCHOOL_NAME, maxSchoolNameWidth);
      splitSchoolName.forEach((line, i) => {
        doc.text(line, xPos + 23, yPos + 8 + (i * lineHeight));
      });
      
      doc.setFont("helvetica", "bold");  // Use a standard bold font
      doc.setFontSize(18);

      // Calculate text width for perfect centering
      const text = "Student Pass";
      const textWidth = doc.getStringUnitWidth(text) * doc.internal.getFontSize() / doc.internal.scaleFactor;
      const centerX = xPos + (85 / 2) - (textWidth / 2);  // 85 is your card width

      // Draw smooth text with slight shadow for depth
      doc.setTextColor(50, 50, 50);  // Dark gray for better printing
      doc.text(text, centerX, yPos + 14 + (splitSchoolName.length - 1) * lineHeight);

      // Reset to normal font for other text
      doc.setFont("helvetica", "normal");
      
      // Student info
      doc.setFontSize(10);
      doc.text(`Name: ${student.firstName} ${student.lastName}`, xPos + 5, yPos + 24);
      doc.text(`Class: ${classInfo.name}`, xPos + 5, yPos + 29);    
      doc.text(`Adm No: ${student.admissionNumber}`, xPos + 5, yPos + 34);
      doc.text(`${classInfo.section} Section - ${classInfo.term} Term - ${classInfo.session}`, xPos + 5, yPos + 39);
     
      // Move position for next pass
      xPos += 90;
      passesInRow++;
    });
  
    doc.save(`Student_Passes_${classInfo.name}_${currentDate.replace(/[/:, ]/g, '_')}.pdf`);
  };

  const fetchPaymentHistory = async (student) => {
    try {
      setFetchingHistory(true);
      setPaymentHistory([]); // Clear history before fetching
      const historyRef = collection(db, "paymentHistory");
      const q = query(historyRef, where("studentName", "==", `${student.firstName} ${student.lastName}`));
      const historySnap = await getDocs(q);
      const history = historySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      history.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
      setPaymentHistory(history);
    } catch (error) {
      setError("Error fetching payment history");
      setShowModal(true);
    } finally {
      setFetchingHistory(false);
    }
  };

  const openTeacherModal = (subject) => {
    setSelectedSubject(subject);
    setShowTeacherModal(true);
  };

  const openQuestionModal = (subject) => {
    setSelectedSubject(subject);
    setShowQuestionModal(true);
  };

  const openPDFModal = (subject) => {
    setSelectedSubject(subject);
    setShowPDFModal(true);
  };
  
  if (loading) return <Loader text="LOADING" />;
  if (!classInfo) return <p className='bold caps align-center justify-center'>Class not found</p>;

  return (
    <div className="login-page-items column align-center">
        <div className="top-elements row align-center space-between">
          <button onClick={() => navigate(-1)} className="back-link back-link1">
              <i className="fa-solid fa-arrow-left size3"></i>
          </button>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>

        <h2 className="manage-page-heading size4">{classInfo.name} ({classInfo.session}-{classInfo.term} Term)</h2>
        <div className="tab-nav row align-center gap-2">
          <button className={`tab-button ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>Info</button>
          {(userData?.status !== 'Cashier') && (
          <button className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`} onClick={() => setActiveTab('subjects')}>Subjects</button>
          )}
          <button className={`tab-button ${activeTab === 'students' ? 'active' : ''}`} onClick={() => setActiveTab('students')}>Students</button>          
        </div>
        
        <AddStudentsToClassModal
          isOpen={showStudentModal}
          classId={id}
          onClose={() => setShowStudentModal(false)}
          onSuccess={fetchData}
        />
        <AddSubjectsToClassModal
          isOpen={showSubjectModal}
          classId={id}
          onClose={() => setShowSubjectModal(false)}
          onSuccess={fetchData}
        />
        <AddTeacherToSubjectModal
          isOpen={showTeacherModal}
          onClose={() => setShowTeacherModal(false)}
          classId={id}
          subject={selectedSubject}
          teachers={teachers} 
          currentTerm={currentTerm}
          onSuccess={fetchData}
        />
        <AssignTeachersToSubjectsModal
          isOpen={showBulkTeacherModal}
          onClose={() => setShowBulkTeacherModal(false)}
          classId={id}
          className={classInfo.name}
          subjects={subjects}
          currentTerm={currentTerm}
          onSuccess={fetchData}
        />
        <AddQuestionsToSubjectModal
          isOpen={showQuestionModal}
          onClose={() => setShowQuestionModal(false)}
          classId={id}
          subject={selectedSubject}
          onSuccess={fetchData}
        />
        <AddPDFToSubjectModal
          isOpen={showPDFModal}
          onClose={() => setShowPDFModal(false)}
          classId={id}
          subject={selectedSubject}
          onSuccess={fetchData}
        />
        <PromoteClassModal
          isOpen={showPromoteModal}
          onClose={() => setShowPromoteModal(false)}
          classId={id}
          currentClass={classInfo}
          students={students}
          subjects={subjects}
          onSuccess={() => {
            fetchData();
            navigate('/classes');
          }}
        />

      <div className="tab-content scroll-fixed scroll-fixed2">
        {activeTab === 'info' && <ClassInfo
            classInfo={classInfo}
            studentCount={students.length}
            subjectCount={subjects.length}
            expectedIncome={financialTotals.expectedIncome}
            actualIncome={financialTotals.actualIncome}
            outstandingBalance={financialTotals.outstandingBalance}
            setShowPromoteModal={setShowPromoteModal}
        />}
        {activeTab === 'students' && (
          <div className="tab-section">
            <div className="tab-section-top column align-center">
              <button className='tab-section-button caps' onClick={() => setShowStudentModal(true)}>Manage Students</button>
            </div>
            {students.length === 0 ? <p className='text-center male-female-stat'>No students enrolled</p> : (
              <div>
                {students.map(stu => (
                  <div key={stu.id} className="info-item info-item1">
                    <span className="font-gold bold size5">{stu.admissionNumber}</span><br />
                    <span>{stu.firstName} {stu.lastName} {stu.middleName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'subjects' && (
          <div className="tab-section">
            <div className="tab-section-top row gap-1 align-center justify-center">
              <button className='tab-section-button caps' onClick={() => setShowSubjectModal(true)}>Manage Subjects</button>
              <button 
                className='tab-section-button caps' 
                onClick={() => setShowBulkTeacherModal(true)}
              >
                Assign Teachers
              </button>
            </div>
            {subjects.length === 0 ? <p className='text-center male-female-stat'>No subjects assigned</p> : (
              <div>
                {subjects.map(sub => {
                  const assignedTeachers = teachers.filter(teacher => 
                    teacher.teachingAssignments?.some(assignment => 
                      assignment.classId === id && assignment.subjectId === sub.id
                    )
                  );

                  return (
                    <div key={sub.id} className="info-item info-item1 row space-between">
                      <div>
                        <span className="font-gold bold size5">
                          {assignedTeachers.length > 0 
                            ? assignedTeachers.map(t => `${t.firstName} ${t.lastName}`).join(', ') 
                            : 'No teacher'}
                        </span><br />
                        <span>{sub.name} ({sub.code})</span><br />
                        {sub.questionSetId && (
                          <span className="bold size5">CBT</span>
                        )}
                        {sub.pdfLink && (
                          <span className="bold size5"> - PDF</span>
                        )}
                      </div>                         
                      <div className="row align-center gap-1">
                        <button className="list-btn" onClick={() => openTeacherModal(sub)}>
                          <i className="fas fa-user"></i>
                        </button>
                        <button className="list-btn" onClick={() => openPDFModal(sub)}>
                          <i className="fas fa-file-upload"></i>
                        </button>
                        <button className="list-btn" onClick={() => openQuestionModal(sub)}>
                          <i className="fas fa-question-circle"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )} 
        {activeTab === 'fees' && (
          <div className="tab-section">
            <div className="tab-section-top row justify-center align-center gap-15">
              <button 
                className='tab-section-button caps' 
                onClick={() => {
                  setFeesAmount(0);
                  setShowFeesModal(true);
                }}
              >
                Assign Fees
              </button>
              <button 
                className='tab-section-button caps' 
                onClick={generateFeesReportPDF}
              >
                Print Report
              </button>
              <button 
                className='tab-section-button caps' 
                onClick={generateStudentPassesPDF}
              >
                Print Pass
              </button>
            </div>
            
            {students.length === 0 ? (
              <p className='text-center male-female-stat'>No students enrolled</p>
            ) : (
              <div className="fees-table-container">                
                <table className="fees-table">
                  <thead>
                    <tr>
                      <th style={{ width: "200px" }}>Student</th>
                      <th style={{ width: "150px" }}>Fees</th>
                      <th style={{ width: "150px" }}>Paid</th>
                      <th style={{ width: "150px" }}>Bal</th>
                      <th style={{ width: "150px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => {
                      const enrollment = student.enrollments?.find(e => e.classId === id) || {
                        fees: 0,
                        paid: 0
                      };
                      
                      const fees = Number(enrollment.fees) || 0;
                      const paid = Number(enrollment.paid) || 0;
                      const balance = fees - paid;

                      return (
                        <tr key={student.id}>
                          <td style={{ width: "200px" }}>{student.firstName} {student.lastName}</td>
                          <td style={{ width: "150px" }}>{fees.toLocaleString()}</td>
                          <td style={{ width: "150px" }}>{paid.toLocaleString()}</td>
                          <td style={{ width: "150px" }}>{balance.toLocaleString()}</td>
                          <td style={{ width: "150px" }}>
                            <div className='row gap-1'>
                              <button 
                                className="list-btn"
                                onClick={() => {
                                  setCurrentStudentFees(student);
                                  setFeesAmount(fees);
                                  setIsEditStudentFeesModalOpen(true);
                                }}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                className="list-btn"
                                onClick={() => {
                                  setCurrentStudentFees(student);
                                  setPaidAmount(0);
                                  setPayerName('');
                                  setPaymentFor(['School fees']);
                                  setIsPayStudentFeesModalOpen(true);
                                }}
                              >
                                <i className="fas fa-money-bill"></i>
                              </button>
                              <button 
                                className="list-btn"
                                onClick={() => {
                                  setCurrentStudentFees(student);
                                  setShowHistoryModal(true);
                                  fetchPaymentHistory(student);
                                }}
                              >
                                <i className="fas fa-history"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}       
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
      {/* Assign Fees to Class Modal */}
      <Modal
        isOpen={showFeesModal}
        onRequestClose={() => setShowFeesModal(false)}
        contentLabel="Assign Fees to Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Assign Fees to <strong>{classInfo.name}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Enter Fees</label>
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount === 0 ? '' : feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setShowFeesModal(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    await Promise.all(
                      students.map(async (student) => {
                        const studentRef = doc(db, "students", student.id);
                        const studentDoc = await getDoc(studentRef);
                        const studentData = studentDoc.data();
                        
                        let enrollments = studentData.enrollments || [];
                        const existingIndex = enrollments.findIndex(e => e.classId === id);
                        
                        if (existingIndex >= 0) {
                          enrollments[existingIndex] = {
                            ...enrollments[existingIndex],
                            fees: Number(feesAmount)
                          };
                        } else {
                          enrollments.push({
                            classId: id,
                            fees: Number(feesAmount || 0),
                            paid: 0
                          });
                        }
                        
                        await updateDoc(studentRef, { enrollments });
                      })
                    );
                    
                    setShowFeesModal(false);
                    fetchData();
                  } catch (error) {
                    setError("Error assigning fees");
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Assign Fees
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Student Fees Modal */}
      <Modal
        isOpen={isEditStudentFeesModalOpen}
        onRequestClose={() => setIsEditStudentFeesModalOpen(false)}
        contentLabel="Edit Student Fees"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Update fees for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Fees Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setIsEditStudentFeesModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const studentRef = doc(db, "students", currentStudentFees.id);
                    const studentDoc = await getDoc(studentRef);
                    const studentData = studentDoc.data();
                    
                    let enrollments = studentData.enrollments || [];
                    const existingIndex = enrollments.findIndex(e => e.classId === id);
                    const currentFees = existingIndex >= 0 ? Number(enrollments[existingIndex].fees) || 0 : 0;
                    const currentPaid = existingIndex >= 0 ? Number(enrollments[existingIndex].paid) || 0 : 0;
                    const balanceAfterChange = Number(feesAmount) - currentPaid;
                    
                    if (existingIndex >= 0) {
                      enrollments[existingIndex] = {
                        ...enrollments[existingIndex],
                        fees: Number(feesAmount)
                      };
                    } else {
                      enrollments.push({
                        classId: id,
                        fees: Number(feesAmount),
                        paid: 0
                      });
                    }
                    
                    await updateDoc(studentRef, { enrollments });

                    if (currentFees !== Number(feesAmount)) {
                      await addDoc(collection(db, "paymentHistory"), {
                        cashierName: `${userData.firstName} ${userData.lastName}`,
                        payerName: "System Update",
                        studentName: `${currentStudentFees.firstName} ${currentStudentFees.lastName}`,
                        className: classInfo.name,
                        term: classInfo.term,
                        session: classInfo.session,
                        previousFees: currentFees,
                        newFees: Number(feesAmount),
                        balance: balanceAfterChange,
                        timestamp: new Date(),
                        type: 'fee_update'
                      });
                    }
                    
                    setIsEditStudentFeesModalOpen(false);
                    fetchData();
                  } catch (error) {
                    setError("Error updating student fees");
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Update Fees
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Pay Student Fees Modal */}      
      <Modal
        isOpen={isPayStudentFeesModalOpen}
        onRequestClose={() => setIsPayStudentFeesModalOpen(false)}
        contentLabel="Pay Student Fees"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Payment for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="Amount"
              value={paidAmount === 0 ? '' : paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
            />
            <label className='text-left'>Payer's Name</label>
            <input
              type="text"
              className="input-field"
              placeholder="Payer's Name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
            />
            <label className='text-left'>Payment For: &nbsp;</label>
            <div className="checkbox-group column gap-10">
              {paymentForOptions.map(option => (
                <label key={option}>
                  <input
                    type="checkbox"
                    className="input-checkbox gold-tick male-female-stat"
                    checked={paymentFor.includes(option)}
                    onChange={() => {
                      setPaymentFor(prev =>
                        prev.includes(option)
                          ? prev.filter(item => item !== option)
                          : [...prev, option]
                      );
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setIsPayStudentFeesModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const studentRef = doc(db, "students", currentStudentFees.id);
                    const studentDoc = await getDoc(studentRef);
                    const studentData = studentDoc.data();
                    
                    let enrollments = studentData.enrollments || [];
                    const existingIndex = enrollments.findIndex(e => e.classId === id);
                    const currentPaid = existingIndex >= 0 ? Number(enrollments[existingIndex].paid) || 0 : 0;
                    const currentFees = existingIndex >= 0 ? Number(enrollments[existingIndex].fees) || 0 : 0;
                    const newPaidAmount = currentPaid + Number(paidAmount || 0);
                    const balanceAfterPayment = currentFees - newPaidAmount;
                    
                    if (existingIndex >= 0) {
                      enrollments[existingIndex] = {
                        ...enrollments[existingIndex],
                        paid: newPaidAmount
                      };
                    } else {
                      enrollments.push({
                        classId: id,
                        fees: 0,
                        paid: newPaidAmount
                      });
                    }
                    
                    await updateDoc(studentRef, { enrollments });

                    if (Number(paidAmount) > 0) {
                      const paymentForData = paymentFor.length > 0 ? paymentFor : ['School fees'];
                      await addDoc(collection(db, "paymentHistory"), {
                        cashierName: `${userData.firstName} ${userData.lastName}`,
                        payerName: payerName || "Unknown",
                        studentName: `${currentStudentFees.firstName} ${currentStudentFees.lastName}`,
                        className: classInfo.name,
                        term: classInfo.term,
                        session: classInfo.session,
                        amountPaid: Number(paidAmount),
                        balance: balanceAfterPayment,
                        paymentFor: paymentForData,
                        timestamp: new Date(),
                        type: 'payment'
                      });
                    }
                    
                    setIsPayStudentFeesModalOpen(false);
                    setPayerName('');
                    setPaymentFor(['School fees']);
                    fetchData();
                  } catch (error) {
                    setError("Error recording payment");
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Payment History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onRequestClose={() => setShowHistoryModal(false)}
        contentLabel="Payment History"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Payment history for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            {fetchingHistory ? (
              <div className="column align-center justify-center">
                <p>Fetching . . .</p>
              </div>
            ) : paymentHistory.length === 0 ? (
              <p>No payment history available</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {paymentHistory.map(payment => (
                  <div key={payment.id} style={{border: 'none'}} className="info-item">
                    <div>
                      {payment.type === 'fee_update' ? (
                        <>
                          <span className="font-gold">FEES UPDATE</span><br />
                          <span>Previous: N {(payment.previousFees ?? 0).toLocaleString()}</span><br />
                          <span>New: N {(payment.newFees ?? 0).toLocaleString()}</span><br />
                          <span className='bold'>Updated by: {payment.cashierName}</span><br />
                        </>
                      ) : (
                        <>
                          <span className='bold'>Cashier: {payment.cashierName}</span><br />
                          <span>Payer: {payment.payerName}</span><br />                          
                          <span>Amount: N {payment.amountPaid.toLocaleString()}</span><br />
                          <span>Balance: N {payment.balance.toLocaleString()}</span><br />
                        </>
                      )}
                      <span className='row space-between align-center'>
                        <span className='size5'>{new Date(payment.timestamp.toDate()).toLocaleString()}</span>
                        {payment.type !== 'fee_update' && (
                          <button 
                            className="list-btn"
                            onClick={() => generateReceiptPDF(payment, userData.firstName, userData.lastName)}
                          >
                            <i className="fas fa-print"></i>
                          </button>
                        )}
                      </span>
                    </div>                    
                  </div>
                ))}
              </div>
            )}
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setShowHistoryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClassDetailsPage;
